import React, { useState, useEffect } from "react";
import "./Testimonials.css";
import { motion, AnimatePresence } from "framer-motion";
import { useSwipeable } from "react-swipeable";
import ReactCountryFlag from "react-country-flag";
import Avatar from "@mui/material/Avatar";
import { FaQuoteLeft, FaStar } from "react-icons/fa";
import { MdArrowBackIos, MdArrowForwardIos } from "react-icons/md";

const QuoteIcon  = FaQuoteLeft       as unknown as React.FC<{ className?: string }>;
const StarIcon   = FaStar            as unknown as React.FC;
const PrevIcon   = MdArrowBackIos    as unknown as React.FC;
const NextIcon   = MdArrowForwardIos as unknown as React.FC;

interface Testimonial {
  name: string;
  country: string;
  countryCode?: string;
  rating: number;
  text: string;
  imageUrl?: string;
  source?: string;
}

const testimonials: Testimonial[] = [
  {
    name: "Nikola",
    country: "Serbia",
    countryCode: "RS",
    rating: 5,
    text: "Everything was just perfect. Frank is something else. So helpful and kind far beyond any expectations. Great location for real vacation. Quiet neighborhood, amazing landscape and backyard. Close to the beach, like 6-7 minutes by car. A lot of places to visit nearby like Olympus, waterfalls, creeks and archeological sites and museums. Cosy and clean interiors with everything you could possibly need. Definitely we'll be coming back on regular basis.",
    imageUrl:
      "https://lh6.googleusercontent.com/-RSwm_vkR4d0/AAAAAAAAAAI/AAAAAAAAABc/JDJhfUq0C5o/s96-c/photo.jpg",
    source: "Booking.com",
  },
  {
    name: "Takev85",
    country: "Romania",
    countryCode: "RO",
    rating: 5,
    text: "Excellent location, not far from the beach, but in a quiet location. Excellent host! Thank you again Frank for your hospitality!",
    imageUrl:
      "https://lh3.googleusercontent.com/a-/AOh14Gg9y0iRxm7K09RajdOa8SNsiNcHhufaOK8WqEK-Dw=s96-c",
    source: "Booking.com",
  },
  {
    name: "Zdravkovic",
    country: "Serbia",
    countryCode: "RS",
    rating: 5,
    text: "Beautiful view, beautiful nature, clean air, comfortable accommodation for the family.",
    source: "Booking.com",
  },
  {
    name: "David",
    country: "Czech Republic",
    countryCode: "CZ",
    rating: 5,
    text: "Great location on the village border with great view to country, Olympos and sea in a distance. Well equipped.",
    imageUrl:
      "https://xx.bstatic.com/xdata/images/xphoto/square64/17173310.jpg?k=fb026eee1e03a51cf4e9ba1a1ef252a14b9c487de5dc18e45ee04b51d29fcba9&o=",
    source: "Booking.com",
  },
  {
    name: "Eleni",
    country: "Greece",
    countryCode: "GR",
    rating: 5,
    text: "Great place, excellent view, great hospitality from the owner, ideal amenities. I will definitely go again.",
    imageUrl:
      "https://lh3.googleusercontent.com/a-/ALV-UjXIbcBsg7pLQf6hdE1FbnYFoheEeKp8tlyjYYaFEKJGzKU7famx=w79-h79-p-rp-mo-ba4-br100",
    source: "Google Reviews",
  },
  {
    name: "Mirek",
    country: "Czech Republic",
    countryCode: "CZ",
    rating: 5,
    text: "All photos correspond to reality. Great place, a bit out of the way, no crowds, yet close to everything. The views are really beautiful, on one side there is a view of the sea, on the other side there is a view of the mountains. The host exceeds your wildest expectations. Even in no hotel there is such service. When it comes to the garden, it is the apple of the owner's eye. I would give 6 stars especially for this facility.",
    source: "Google Reviews",
  },
];

const AUTO_ADVANCE_MS = 7000;

function stringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i++) {
    color += ("00" + ((hash >> (i * 8)) & 0xff).toString(16)).slice(-2);
  }
  return color;
}

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<"left" | "right">("right");
  const [paused, setPaused] = useState(false);

  // Auto-advance: resets whenever currentIndex or paused changes
  useEffect(() => {
    if (paused) return;
    const id = setTimeout(() => {
      setDirection("right");
      setCurrentIndex((i) => (i + 1) % testimonials.length);
    }, AUTO_ADVANCE_MS);
    return () => clearTimeout(id);
  }, [paused, currentIndex]);

  const prev = () => {
    setDirection("left");
    setCurrentIndex((i) => (i === 0 ? testimonials.length - 1 : i - 1));
  };

  const next = () => {
    setDirection("right");
    setCurrentIndex((i) => (i + 1) % testimonials.length);
  };

  const goTo = (index: number) => {
    setDirection(index > currentIndex ? "right" : "left");
    setCurrentIndex(index);
  };

  const handlers = useSwipeable({
    onSwipedLeft: next,
    onSwipedRight: prev,
    trackMouse: true,
  });

  const t = testimonials[currentIndex];

  return (
    <section
      className="tst-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="tst-header">
        <h2 className="tst-title">What Our Guests Say</h2>
        <div className="tst-title-bar" />
      </div>

      <div className="tst-stage" {...handlers}>
        <button className="tst-arrow tst-arrow--prev" onClick={prev} aria-label="Previous review">
          <PrevIcon />
        </button>

        <motion.div className="tst-track" layout transition={{ duration: 0.38, ease: "easeInOut" }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              className="tst-card"
              layout
              initial={{ opacity: 0, x: direction === "right" ? 70 : -70 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction === "right" ? -70 : 70 }}
              transition={{ duration: 0.38, ease: "easeInOut" }}
            >
              <div className="tst-content">
                <QuoteIcon className="tst-quote-icon" />
                <p className="tst-text">{t.text}</p>
              </div>

              <div className="tst-footer">
                <div className="tst-reviewer">
                  <Avatar
                    src={t.imageUrl}
                    alt={t.name}
                    sx={{
                      width: 46,
                      height: 46,
                      bgcolor: t.imageUrl ? undefined : stringToColor(t.name),
                      fontSize: "1rem",
                    }}
                  >
                    {!t.imageUrl && t.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <div className="tst-reviewer-info">
                    <span className="tst-name">{t.name}</span>
                    <span className="tst-country">
                      {t.countryCode && (
                        <ReactCountryFlag
                          countryCode={t.countryCode}
                          svg
                          style={{ fontSize: "1rem", marginRight: "4px" }}
                          aria-label={t.country}
                        />
                      )}
                      {t.country}
                    </span>
                  </div>
                </div>

                <div className="tst-rating-col">
                  <div className="tst-stars" aria-label={`${t.rating} out of 5 stars`}>
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <StarIcon key={i} />
                    ))}
                  </div>
                  {t.source && (
                    <span className="tst-source">via {t.source}</span>
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.div>

        <button className="tst-arrow tst-arrow--next" onClick={next} aria-label="Next review">
          <NextIcon />
        </button>
      </div>

      <div className="tst-dots" role="tablist" aria-label="Select review">
        {testimonials.map((_, i) => (
          <button
            key={i}
            role="tab"
            aria-selected={i === currentIndex}
            aria-label={`Review ${i + 1}`}
            className={`tst-dot ${i === currentIndex ? "tst-dot--active" : ""}`}
            onClick={() => goTo(i)}
          />
        ))}
      </div>

      <p className="tst-footer-text">
        We are proud of our guests' feedback — come stay with us!
      </p>
    </section>
  );
};

export default Testimonials;
